import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { ReportData } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { createTableReport } from 'components/ApiTable/model/commands/createTableReport';
import { deleteTableReports } from 'components/ApiTable/model/commands/deleteTableReports';
import { handleColorFilterUpdate } from 'components/ApiTable/model/commands/handleColorFilterUpdate';
import { reloadTableDataInViewport } from 'components/ApiTable/model/commands/reloadTableDataInViewport';
import { selectTableId } from 'components/ApiTable/model/selectors/selectTableId';
import { ipfSocket, joinRoom, leaveRoom } from 'components/Socket/ipfSocket';
import { useEffect } from 'react';

export const useIntentVerificationSocket = (): void => {
  const ctxProps = useApiTableContextSelectors({
    snapshotId: prop('snapshotId'),
    tableId: selectTableId,
    reports: prop('reports'),
    dispatch: prop('dispatch'),
    onChangeIntentFilter: prop('tableModel', 'onChangeIntentFilter'),
  });

  const roomName = `colorFilter:snapshot=${ctxProps.snapshotId}:webEndpoint=${ctxProps.tableId}`;

  const handleUpdate = useStableCallback((data: ReportData[]) => {
    ctxProps.dispatch(handleColorFilterUpdate, { data });
  });

  const handleDelete = useStableCallback(
    (deletedReports: { id: string; column: string }[]) => {
      if (!ctxProps.onChangeIntentFilter) {
        stumble(
          'onChangeIntentFilter was not provided into Table from ApiTable',
        );
        return;
      }

      ctxProps.dispatch(deleteTableReports, { deletedReports });
      for (const deletedReport of deletedReports) {
        if (deletedReport.column) {
          ctxProps.onChangeIntentFilter?.({
            column: deletedReport.column,
            severity: null,
          });
        }
      }
      ctxProps.dispatch(reloadTableDataInViewport);
    },
  );

  const handleCreate = useStableCallback(
    (data: Omit<ReportData, 'status'>[]) => {
      ctxProps.dispatch(createTableReport, { data });
    },
  );

  const setupSockets = useStableCallback((currentRoomName: string) => {
    joinRoom(currentRoomName);
    ipfSocket.socket.on('colorFilter:updated', handleUpdate);
    ipfSocket.socket.on('colorFilter:deleted', handleDelete);
    ipfSocket.socket.on('colorFilter:created', handleCreate);
  });

  useEffect(() => {
    const setupSocketsEffect = () => setupSockets(roomName);
    if (ipfSocket.socketIsConnected) {
      setupSocketsEffect();
    }
    ipfSocket.socket.on('authenticated', setupSocketsEffect);

    return () => {
      leaveRoom(roomName);
      ipfSocket.socket.off('colorFilter:updated', handleUpdate);
      ipfSocket.socket.off('colorFilter:deleted', handleDelete);
      ipfSocket.socket.off('colorFilter:created', handleCreate);
      ipfSocket.socket.off('authenticated', setupSocketsEffect);
    };
  }, [roomName]);
};
